/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { AppConfig } from '@/types/config';

/** Origin GTM serves `gtm.js` and the `noscript` iframe from. */
export const GTM_ORIGIN = 'https://www.googletagmanager.com';

export type GoogleTagManagerConfig = NonNullable<AppConfig['engagement']['googleTagManager']>;

/**
 * Container IDs are uppercase alphanumeric with a `GTM-` prefix.
 *
 * This is a strict allowlist, not a friendly format check. The resolved value is
 * interpolated into an inline `<script>` body and into the `noscript` iframe `src`, so
 * anything that escapes this pattern would be script injection via an environment
 * variable. Reject rather than sanitize: a container ID that fails this test is a
 * misconfiguration, and the correct response is to install no container at all.
 */
const CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]{4,20}$/;

/**
 * Pure, non-logging validity check. The single definition of "usable container ID".
 *
 * Takes `unknown`, not `string`, on purpose. `GoogleTagManagerConfig` *declares* string
 * values, but nothing enforces that at runtime: `PUBLIC__` overrides are applied by the SDK's
 * env merge **after** `config.server.ts` is evaluated, and it JSON-parses every value — so
 * `…__containerId=12345` arrives as the number `12345`, and `…__sites='{"SITE":1234}'` as
 * `{SITE: 1234}`. Sanitizing at the parse site in `config.server.ts` cannot prevent this,
 * because the merge runs later and overwrites it (that is what `protectedPaths` exists to
 * block, and GTM is deliberately not protected so each MRT target can set its own container).
 *
 * Without the `typeof` guard those values reach `.trim()` and throw a TypeError — during SSR
 * render for the resolver, and during security-middleware construction for the CSP
 * contributor. Both are the opposite of the fail-closed behaviour this module promises.
 */
function isValidContainerId(value: unknown): value is string {
    return typeof value === 'string' && CONTAINER_ID_PATTERN.test(value.trim());
}

/**
 * Explicit opt-in check for the `enabled` switch — deliberately not a truthiness test.
 *
 * Same reason `isValidContainerId` takes `unknown`: the value that reaches here is the
 * post-merge one, and the SDK env merge parses `PUBLIC__` values optimistically as JSON,
 * falling back to the raw string. `false` and `0` disable correctly, but `False`, `"false"`,
 * `off` and `no` all parse to a truthy *string* — so a truthiness test would keep the
 * container installed for someone who just tried to switch tracking off on a live target,
 * with no error to tell them otherwise. Only an explicit yes counts.
 *
 * Mirrors `isEnabled` in `src/middlewares/csp-contributors/shopper-agent.ts`.
 */
function isEnabled(value: unknown): boolean {
    return value === true || value === 'true';
}

/**
 * Whether any site could load GTM under this config.
 *
 * Exists for consumers that run before a site is known — chiefly the CSP contributor, which is
 * evaluated once at boot. Applies the same validation as `resolveGtmContainerId`, so the two
 * cannot drift and a config whose only container IDs are unusable leaves the policy untouched
 * rather than relaxing it for an origin no page will ever contact.
 *
 * Non-object `sites` is safe here: `Object.values` of a number or string never throws.
 */
export function isGtmConfigured(config: GoogleTagManagerConfig | undefined): boolean {
    if (!config || !isEnabled(config.enabled)) return false;
    return isValidContainerId(config.containerId) || Object.values(config.sites ?? {}).some(isValidContainerId);
}

/**
 * Resolve the GTM container ID for the active site, or `null` when GTM should not load.
 *
 * Resolution order:
 *   1. `sites[siteId]` — per-site override, so a second brand reports into its own container
 *   2. `containerId`   — the environment's default container
 *   3. `null`          — nothing configured, or the configured value failed validation
 *
 * Returning `null` is the fail-closed path: no container ID resolved means no GTM script is
 * rendered. An environment that was never given a container ID reports nothing, rather than
 * inheriting a committed default and polluting another environment's analytics.
 *
 * @param config - the resolved `engagement.googleTagManager` config block (may be undefined)
 * @param siteId - active site ID, used to look up a per-site override
 */
export function resolveGtmContainerId(config: GoogleTagManagerConfig | undefined, siteId?: string): string | null {
    if (!config || !isEnabled(config.enabled)) return null;

    // Indexing a non-object `sites` yields undefined rather than throwing, so a malformed
    // `sites` degrades to the default container instead of taking the page down. A per-site
    // value that is present but not a string is NOT nullish, so it does not fall back to the
    // default — it fails validation below, preserving the documented rule that a bad override
    // installs nothing rather than reporting into the wrong container.
    const perSite = siteId ? config.sites?.[siteId] : undefined;
    const raw = perSite ?? config.containerId;
    const candidate = typeof raw === 'string' ? raw.trim() : raw;

    // Unset or blank: GTM is simply not configured on this environment. Expected, not an error.
    if (candidate === undefined || candidate === null || candidate === '') return null;

    if (!isValidContainerId(candidate)) {
        // eslint-disable-next-line no-console
        console.error(
            `[GTM] Ignoring malformed container ID "${String(candidate)}" — expected the form GTM-XXXXXXX. No container installed.`
        );
        return null;
    }

    return candidate;
}
